import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { Project } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { useCan } from '@/lib/permissions';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArchiveProjectDialog } from '@/features/projects/ArchiveProjectDialog';

/**
 * Design 28 · Danger zone: archive the project. The confirmation is the shared ArchiveProjectDialog (impact summary
 * with live counts, its apps archived with it by default, the slug typed to confirm); afterwards the page leaves for
 * the Overview, since an archived project has no settings to show.
 */
export function DangerZoneCard({ project, id, index }: { project: Project; id: string; index: number }) {
  const can = useCan('projects:write', project.id);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (can.hidden) return null;
  return (
    <Card id={id} data-testid="settings-danger" className="enter scroll-mt-24 border-destructive/30" style={stagger(index)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <Icon name="warning" size={20} />
          Danger zone
        </CardTitle>
        <CardDescription>Actions that take the project out of service</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 border-destructive/20 sm:flex-row sm:items-end sm:justify-between">
        <ArchiveExplainer project={project} />
        <GatedButton variant="destructive" data-testid="settings-archive" can={can} className="shrink-0" onClick={() => setOpen(true)}>
          <Icon name="archive" size={18} />
          Archive project
        </GatedButton>
      </CardContent>
      <ArchiveProjectDialog project={open ? project : null} onOpenChange={setOpen} onArchived={() => void navigate({ to: '/' })} />
    </Card>
  );
}

function ArchiveExplainer({ project }: { project: Project }) {
  return (
    <div className="min-w-0">
      <h3 className="text-[15px] font-semibold text-foreground">Archive project</h3>
      <p className="mt-1 max-w-[64ch] text-[13px] text-muted-foreground">
        {project.name} leaves every list and the project switcher, and its apps, archived with it, stop taking new builds. Nothing is deleted: builds, artifacts and env snapshots stay.
      </p>
      <p className="mt-2 text-xs text-subtle">
        Requires typing <code className="rounded-sm bg-destructive/10 px-1.5 py-px font-mono text-destructive">{project.slug}</code> to confirm
      </p>
    </div>
  );
}
